// ReactHookFormArray.jsx
import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    IconButton,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactHookFormSelect } from 'common/components/form/RHF';
import TextVariant from 'common/components/TextVariant';
import {
    itemsToSelectRHFWithoutDuplicates,
    nextItemsToSelect,
} from 'common/utils/helpers';

const ReactHookFormArray = ({
    control,
    disabled,
    dot,
    errors, // Add errors prop
    getValues,
    groupName,
    itemsToSelect,
    label,
}) => {
    const { t } = useTranslation();

    const { append, fields, remove } = useFieldArray({
        control: control,
        name: groupName,
    });

    const _disabled =
        disabled ||
        nextItemsToSelect(getValues(groupName), itemsToSelect).length === 0;

    return (
        <Box display="flex" flexDirection="column">
            <Box sx={{ mb: 2 }}>
                <TextVariant dot={dot} text={label} variant="bold" />
            </Box>

            {fields.map((item, i) => (
                <Box display="flex" gap="16px" key={item.id} sx={{ mb: 2 }}>
                    <ReactHookFormSelect
                        control={control}
                        disabled={disabled}
                        name={`${groupName}.${i}.item`}
                        options={itemsToSelectRHFWithoutDuplicates(
                            item.id,
                            getValues(groupName),
                            itemsToSelect,
                        )}
                    />
                    <Box>
                        <IconButton
                            disabled={disabled}
                            onClick={() => remove(i)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            ))}

            {/* Display validation error for the products array */}
            {errors?.[groupName] && fields?.length === 0 && (
                <Typography color="error" variant="caption" sx={{ mb: 2 }}>
                    {errors[groupName].message ||
                        t('At least one product is required')}
                </Typography>
            )}

            <Button
                disabled={_disabled}
                onClick={() => append({ item: '' })}
                type="button"
                variant="outlined"
            >
                {t('Add')}
            </Button>
        </Box>
    );
};

ReactHookFormArray.propTypes = {
    control: PropTypes.object,
    disabled: PropTypes.bool,
    dot: PropTypes.string,
    errors: PropTypes.object, // Add errors prop type
    getValues: PropTypes.func,
    groupName: PropTypes.string,
    itemsToSelect: PropTypes.array,
    label: PropTypes.string,
};

export default ReactHookFormArray;
